class PremiumProductInformation extends HTMLElement {
  connectedCallback() {
    this.classList.add('is-reveal-ready');
    this.mainMedia = this.querySelector('.ppi__main-media');
    this.mainImage = this.querySelector('.ppi__main-image');
    this.thumbsTrack = this.querySelector('[data-ppi-thumbs]');
    this.thumbs = Array.from(this.querySelectorAll('[data-ppi-thumb]'));
    this.variantButtons = Array.from(this.querySelectorAll('[data-ppi-variant]'));
    this.variantInput = this.querySelector('[data-ppi-variant-input]');
    this.quantityInput = this.querySelector('[data-ppi-quantity]');
    this.price = this.querySelector('[data-ppi-price]');
    this.compare = this.querySelector('[data-ppi-compare]');
    this.discount = this.querySelector('[data-ppi-discount]');
    this.buyButton = this.querySelector('[data-ppi-buy-now]');
    this.addButton = this.querySelector('[data-ppi-add-to-cart]');
    this.addComponent = this.querySelector('.ppi__add-component');
    this.rating = this.querySelector('[data-ppi-rating]');
    this.judgeMeBadge = this.querySelector('[data-ppi-judgeme-badge]');
    this.ratingValue = this.querySelector('[data-ppi-rating-value]');
    this.reviewCount = this.querySelector('[data-ppi-review-count]');
    this.error = this.querySelector('[data-ppi-error]');
    this.errorText = this.querySelector('[data-ppi-error-text]');
    this.productForm = this.querySelector('product-form-component');
    this.trustTrack = this.querySelector('.ppi__trust');
    this.trustCards = Array.from(this.querySelectorAll('.ppi__trust-card'));
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.galleryInterval = Number(this.dataset.galleryInterval) || 3000;
    this.trustInterval = Number(this.dataset.trustInterval) || 2000;
    this.galleryIndex = Math.max(0, this.thumbs.findIndex((thumb) => thumb.classList.contains('is-active')));
    this.trustIndex = 0;
    this.isInView = false;
    this.galleryStoppedByUser = false;

    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('submit', this.onProductSubmit, true);
    this.mainMedia?.addEventListener('pointerenter', this.stopGalleryAutoplay);
    this.mainMedia?.addEventListener('pointerleave', this.startGalleryAutoplay);
    this.trustTrack?.addEventListener('pointerdown', this.stopTrustAutoplay);
    this.trustTrack?.addEventListener('pointerup', this.restartTrustAutoplay);
    this.trustTrack?.addEventListener('pointercancel', this.restartTrustAutoplay);
    this.trustTrack?.addEventListener('scroll', this.onTrustScroll, { passive: true });
    this.mobileQuery.addEventListener('change', this.onViewportChange);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.setupJudgeMeRating();
    this.setupObserver();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('pointerdown', this.onPointerDown);
    this.removeEventListener('submit', this.onProductSubmit, true);
    this.mainMedia?.removeEventListener('pointerenter', this.stopGalleryAutoplay);
    this.mainMedia?.removeEventListener('pointerleave', this.startGalleryAutoplay);
    this.trustTrack?.removeEventListener('pointerdown', this.stopTrustAutoplay);
    this.trustTrack?.removeEventListener('pointerup', this.restartTrustAutoplay);
    this.trustTrack?.removeEventListener('pointercancel', this.restartTrustAutoplay);
    this.trustTrack?.removeEventListener('scroll', this.onTrustScroll);
    this.mobileQuery?.removeEventListener('change', this.onViewportChange);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.observer?.disconnect();
    this.ratingObserver?.disconnect();
    this.stopGalleryAutoplay();
    this.stopTrustAutoplay();
    clearTimeout(this.galleryRestartTimer);
    clearTimeout(this.trustRestartTimer);
    clearTimeout(this.trustScrollTimer);
    clearTimeout(this.imageChangeTimer);
    clearTimeout(this.buttonResetTimer);
    this.cartRequest?.abort();
  }

  onClick = (event) => {
    const variant = event.target.closest('[data-ppi-variant]');
    if (variant && this.contains(variant)) {
      this.selectVariant(variant);
      return;
    }

    const thumb = event.target.closest('[data-ppi-thumb]');
    if (thumb && this.contains(thumb)) {
      this.stopGalleryForUser();
      this.activateThumb(thumb, true);
      return;
    }

    if (event.target.closest('[data-ppi-thumb-previous]')) {
      this.stopGalleryForUser();
      this.moveGallery(-1);
      return;
    }

    if (event.target.closest('[data-ppi-thumb-next]')) {
      this.stopGalleryForUser();
      this.moveGallery(1);
      return;
    }

    const mainMedia = event.target.closest('[data-ppi-main-media]');
    if (mainMedia && this.contains(mainMedia)) {
      this.stopGalleryForUser();
      mainMedia.classList.remove('is-clicked');
      requestAnimationFrame(() => mainMedia.classList.add('is-clicked'));
      setTimeout(() => mainMedia.classList.remove('is-clicked'), 420);
      return;
    }

    if (event.target.closest('[data-ppi-quantity-minus]')) {
      this.changeQuantity(-1);
      return;
    }

    if (event.target.closest('[data-ppi-quantity-plus]')) {
      this.changeQuantity(1);
      return;
    }

    const buyButton = event.target.closest('[data-ppi-buy-now]');
    if (buyButton && this.contains(buyButton)) {
      this.buyNow();
      return;
    }

    const addButton = event.target.closest('[data-ppi-add-to-cart]');
    if (addButton && this.contains(addButton)) this.prepareAddToCart(addButton);
  };

  onChange = (event) => {
    if (event.target !== this.quantityInput) return;
    this.normalizeQuantity();
  };

  onPointerDown = (event) => {
    const button = event.target.closest('[data-ppi-buy-now], [data-ppi-add-to-cart], [data-ppi-variant]');
    if (!button || !this.contains(button)) return;
    this.addRipple(button, event);
  };

  onVisibilityChange = () => {
    if (document.hidden) {
      this.stopGalleryAutoplay();
      this.stopTrustAutoplay();
    } else {
      this.startGalleryAutoplay();
      this.startTrustAutoplay();
    }
  };

  onViewportChange = () => {
    this.trustIndex = 0;
    if (this.trustTrack) this.trustTrack.scrollLeft = 0;
    this.startTrustAutoplay();
  };

  onTrustScroll = () => {
    clearTimeout(this.trustScrollTimer);
    this.trustScrollTimer = setTimeout(() => this.updateTrustIndex(), 100);
  };

  selectVariant(button) {
    if (button.disabled) return;

    this.variantButtons.forEach((variant) => {
      const selected = variant === button;
      variant.classList.toggle('is-selected', selected);
      variant.setAttribute('aria-checked', selected ? 'true' : 'false');
    });

    const available = button.dataset.available === 'true';
    const variantId = button.dataset.variantId;
    if (this.variantInput) {
      this.variantInput.value = variantId;
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (this.price) this.price.textContent = button.dataset.price || '';
    this.updateComparePrice(button);
    this.updateAvailability(available);
    this.updateQuantityRules(button);

    const imageSrc = button.dataset.imageSrc;
    if (imageSrc) {
      const matchingThumb = this.thumbs.find((thumb) => thumb.dataset.mediaId === button.dataset.mediaId);
      if (matchingThumb) this.activateThumb(matchingThumb, false);
      else this.changeMainImage(imageSrc, button.dataset.imageAlt || '');
      this.addComponent?.setAttribute('data-product-variant-media', imageSrc);
    }

    this.clearError();
  }

  updateComparePrice(button) {
    const priceCents = Number(button.dataset.priceCents) || 0;
    const compareCents = Number(button.dataset.compareCents) || 0;
    const onSale = compareCents > priceCents && compareCents > 0;

    if (this.compare) {
      this.compare.textContent = onSale ? button.dataset.compare || '' : '';
      this.compare.classList.toggle('is-hidden', !onSale);
    }

    if (this.discount) {
      const suffix = this.discount.textContent.replace(/^\d+%\s*/, '') || 'OFF';
      const percentage = onSale ? Math.round(((compareCents - priceCents) / compareCents) * 100) : 0;
      this.discount.textContent = `${percentage}% ${suffix}`;
      this.discount.classList.toggle('is-hidden', !onSale);
    }
  }

  updateAvailability(available) {
    if (this.buyButton) this.buyButton.disabled = !available;
    if (this.addButton) {
      this.addButton.disabled = !available;
      const label = this.addButton.querySelector('[data-ppi-add-label]');
      if (label) {
        if (!this.addButton.dataset.availableLabel) this.addButton.dataset.availableLabel = label.textContent.trim();
        label.textContent = available ? this.addButton.dataset.availableLabel : 'Sold out';
      }
    }
  }

  updateQuantityRules(button) {
    if (!this.quantityInput) return;
    const minimum = Number(button.dataset.quantityMin) || 1;
    const maximum = Number(button.dataset.quantityMax) || null;
    const step = Number(button.dataset.quantityStep) || 1;
    this.quantityInput.min = String(minimum);
    this.quantityInput.step = String(step);
    if (maximum) this.quantityInput.max = String(maximum);
    else this.quantityInput.removeAttribute('max');
    this.normalizeQuantity();
  }

  changeQuantity(direction) {
    if (!this.quantityInput) return;
    const step = Number(this.quantityInput.step) || 1;
    this.quantityInput.value = String((Number(this.quantityInput.value) || 1) + direction * step);
    this.normalizeQuantity();
  }

  normalizeQuantity() {
    if (!this.quantityInput) return 1;
    const minimum = Number(this.quantityInput.min) || 1;
    const maximum = Number(this.quantityInput.max) || Number.POSITIVE_INFINITY;
    const step = Number(this.quantityInput.step) || 1;
    let quantity = Number(this.quantityInput.value) || minimum;
    quantity = Math.max(minimum, Math.min(maximum, quantity));
    quantity = minimum + Math.round((quantity - minimum) / step) * step;
    quantity = Math.max(minimum, Math.min(maximum, quantity));
    this.quantityInput.value = String(quantity);
    return quantity;
  }

  activateThumb(thumb, userInitiated = false) {
    const index = this.thumbs.indexOf(thumb);
    if (index < 0) return;
    this.galleryIndex = index;
    this.thumbs.forEach((item) => {
      const active = item === thumb;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    this.changeMainImage(thumb.dataset.imageSrc, thumb.dataset.imageAlt || '');
    this.scrollThumbWithinTrack(thumb);
    if (userInitiated) this.stopGalleryForUser();
  }

  scrollThumbWithinTrack(thumb) {
    const track = this.thumbsTrack;
    if (!track) return;

    const trackRect = track.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    let nextLeft = track.scrollLeft;
    let nextTop = track.scrollTop;

    if (thumbRect.left < trackRect.left) nextLeft += thumbRect.left - trackRect.left;
    else if (thumbRect.right > trackRect.right) nextLeft += thumbRect.right - trackRect.right;

    if (thumbRect.top < trackRect.top) nextTop += thumbRect.top - trackRect.top;
    else if (thumbRect.bottom > trackRect.bottom) nextTop += thumbRect.bottom - trackRect.bottom;

    if (nextLeft === track.scrollLeft && nextTop === track.scrollTop) return;
    track.scrollTo({
      left: Math.max(0, nextLeft),
      top: Math.max(0, nextTop),
      behavior: this.reducedMotionQuery.matches ? 'auto' : 'smooth',
    });
  }

  changeMainImage(src, alt) {
    if (!this.mainImage || !src || this.mainImage.currentSrc === src || this.mainImage.src === src) return;
    clearTimeout(this.imageChangeTimer);
    this.mainMedia?.classList.add('is-changing');
    this.imageChangeTimer = setTimeout(() => {
      this.mainImage.removeAttribute('srcset');
      this.mainImage.removeAttribute('sizes');
      this.mainImage.src = src;
      this.mainImage.alt = alt;
      requestAnimationFrame(() => this.mainMedia?.classList.remove('is-changing'));
    }, this.reducedMotionQuery.matches ? 0 : 150);
  }

  moveGallery(direction) {
    if (this.thumbs.length < 2) return;
    this.galleryIndex = (this.galleryIndex + direction + this.thumbs.length) % this.thumbs.length;
    this.activateThumb(this.thumbs[this.galleryIndex], false);
  }

  startGalleryAutoplay = () => {
    this.stopGalleryAutoplay();
    if (
      this.dataset.galleryAutoplay !== 'true' ||
      this.galleryStoppedByUser ||
      this.thumbs.length < 2 ||
      !this.isInView ||
      document.hidden ||
      this.reducedMotionQuery.matches
    ) return;
    this.galleryTimer = setInterval(() => this.moveGallery(1), this.galleryInterval);
  };

  stopGalleryAutoplay = () => {
    clearInterval(this.galleryTimer);
    this.galleryTimer = undefined;
  };

  restartGalleryAutoplay() {
    if (this.galleryStoppedByUser) return;
    this.stopGalleryAutoplay();
    clearTimeout(this.galleryRestartTimer);
    this.galleryRestartTimer = setTimeout(this.startGalleryAutoplay, this.galleryInterval);
  }

  stopGalleryForUser() {
    this.galleryStoppedByUser = true;
    clearTimeout(this.galleryRestartTimer);
    this.stopGalleryAutoplay();
    this.mainMedia?.classList.add('is-user-paused');
    this.mainMedia?.setAttribute('title', 'Automatic image rotation paused');
  }

  setupJudgeMeRating() {
    if (!this.rating || !this.judgeMeBadge) return;
    this.updateJudgeMeRating();
    this.ratingObserver = new MutationObserver(this.updateJudgeMeRating);
    this.ratingObserver.observe(this.judgeMeBadge, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['data-average-rating', 'data-number-of-reviews', 'data-score'],
    });
  }

  updateJudgeMeRating = () => {
    if (!this.rating) return;
    const badge = this.judgeMeBadge?.querySelector('.jdgm-prev-badge');
    const stars = this.judgeMeBadge?.querySelector('[data-score]');
    const average = Number(badge?.dataset.averageRating || stars?.dataset.score || 0);
    const reviews = Number(badge?.dataset.numberOfReviews || 0);
    const hasReviews = Number.isFinite(average) && average > 0 && Number.isFinite(reviews) && reviews > 0;

    this.rating.classList.toggle('has-reviews', hasReviews);
    if (this.ratingValue) this.ratingValue.textContent = hasReviews ? average.toFixed(1) : '0.0';
    if (this.reviewCount) this.reviewCount.textContent = hasReviews ? reviews.toLocaleString() : '0';
    this.rating.setAttribute(
      'aria-label',
      hasReviews ? `Rated ${average.toFixed(1)} out of 5 from ${reviews} reviews` : 'No product reviews yet'
    );
  };

  startTrustAutoplay = () => {
    this.stopTrustAutoplay();
    if (
      this.dataset.trustAutoplay !== 'true' ||
      !this.mobileQuery.matches ||
      this.trustCards.length < 2 ||
      !this.isInView ||
      document.hidden ||
      this.reducedMotionQuery.matches
    ) return;
    this.trustTimer = setInterval(() => this.advanceTrust(), this.trustInterval);
  };

  stopTrustAutoplay = () => {
    clearInterval(this.trustTimer);
    this.trustTimer = undefined;
  };

  restartTrustAutoplay = () => {
    this.updateTrustIndex();
    this.stopTrustAutoplay();
    clearTimeout(this.trustRestartTimer);
    this.trustRestartTimer = setTimeout(this.startTrustAutoplay, this.trustInterval);
  };

  advanceTrust() {
    if (!this.trustTrack || this.trustCards.length < 2) return;
    this.trustIndex = (this.trustIndex + 1) % this.trustCards.length;
    this.trustTrack.scrollTo({
      left: this.trustCards[this.trustIndex].offsetLeft - this.trustCards[0].offsetLeft,
      behavior: this.reducedMotionQuery.matches ? 'auto' : 'smooth',
    });
  }

  updateTrustIndex() {
    if (!this.trustTrack || this.trustCards.length === 0) return;
    const start = this.trustCards[0].offsetLeft;
    const position = this.trustTrack.scrollLeft + start;
    let distance = Number.POSITIVE_INFINITY;
    this.trustCards.forEach((card, index) => {
      const nextDistance = Math.abs(card.offsetLeft - position);
      if (nextDistance >= distance) return;
      distance = nextDistance;
      this.trustIndex = index;
    });
  }

  prepareAddToCart(button) {
    if (button.disabled) return;
    this.clearError();
    button.classList.remove('is-success', 'is-error');
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    this.pendingAddButton = button;

    clearTimeout(this.buttonResetTimer);
    this.buttonResetTimer = setTimeout(() => {
      if (button.classList.contains('is-loading')) this.resetAddButton(button);
    }, 5000);
  }

  onProductSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !this.contains(form)) return;

    const button = event.submitter?.matches?.('[data-ppi-add-to-cart]') ? event.submitter : this.addButton;
    if (!button) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (button.disabled || button.getAttribute('aria-busy') === 'true' && this.cartRequest) return;
    this.addProductToCart(form, button);
  };

  async addProductToCart(form, button) {
    this.cartRequest?.abort();
    this.cartRequest = new AbortController();
    this.prepareAddToCart(button);

    try {
      const response = await fetch(this.cartAddUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
        credentials: 'same-origin',
        signal: this.cartRequest.signal,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Unable to add this product.');
      }

      this.setAddSuccess(button);
      await this.openCartDrawer();
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.setAddError(button, error?.message || 'Unable to add this product.');
    } finally {
      this.cartRequest = null;
    }
  }

  get cartAddUrl() {
    const route = this.dataset.cartAddUrl || '/cart/add';
    return route.endsWith('.js') ? route : `${route}.js`;
  }

  buyNow() {
    if (!this.buyButton || this.buyButton.disabled || !this.variantInput?.value) return;
    this.clearError();
    this.buyButton.classList.add('is-loading');
    this.buyButton.setAttribute('aria-busy', 'true');

    try {
      const checkoutForm = document.createElement('form');
      checkoutForm.method = 'post';
      checkoutForm.action = this.dataset.cartAddUrl || '/cart/add';
      checkoutForm.hidden = true;

      const fields = {
        id: this.variantInput.value,
        quantity: String(this.normalizeQuantity()),
        return_to: this.dataset.checkoutUrl || '/checkout',
        form_type: 'product',
        utf8: '✓',
      };

      Object.entries(fields).forEach(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        checkoutForm.append(input);
      });

      document.body.append(checkoutForm);
      HTMLFormElement.prototype.submit.call(checkoutForm);
    } catch (error) {
      this.buyButton.classList.remove('is-loading');
      this.buyButton.removeAttribute('aria-busy');
      this.showError(error?.message || 'Checkout could not start. Please try again.');
    }
  }

  openCartDrawer = async () => {
    const premiumHeader = Array.from(document.querySelectorAll('premium-custom-header')).find((header) =>
      header.querySelector('[data-pch-cart-drawer]')
    );
    if (premiumHeader) {
      const openPremiumDrawer = () => {
        if (typeof premiumHeader.openCartDrawer === 'function') return premiumHeader.openCartDrawer();
        return premiumHeader.querySelector('[data-pch-cart-trigger]')?.click();
      };

      if (!customElements.get('premium-custom-header')) await customElements.whenDefined('premium-custom-header');
      await openPremiumDrawer();
      return;
    }

    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (!drawer) {
      document.querySelector('[data-testid="cart-drawer-trigger"]')?.click();
      return;
    }
    const open = () => {
      if (!drawer.isOpen && typeof drawer.open === 'function') drawer.open();
    };
    if (customElements.get('theme-drawer')) open();
    else customElements.whenDefined('theme-drawer').then(open);
  };

  setAddSuccess(button) {
    if (!button) return;
    button.classList.remove('is-loading', 'is-error');
    button.classList.add('is-success');
    button.removeAttribute('aria-busy');
    this.pendingAddButton = null;
    clearTimeout(this.buttonResetTimer);
    this.buttonResetTimer = setTimeout(() => button.classList.remove('is-success'), 1500);
  }

  setAddError(button, message) {
    if (button) {
      button.classList.remove('is-loading', 'is-success');
      button.classList.add('is-error');
      button.removeAttribute('aria-busy');
      setTimeout(() => button.classList.remove('is-error'), 900);
    }
    this.pendingAddButton = null;
    this.showError(message);
  }

  resetAddButton(button) {
    button?.classList.remove('is-loading');
    button?.removeAttribute('aria-busy');
    this.pendingAddButton = null;
  }

  showError(message) {
    if (!this.error) return;
    if (this.errorText) this.errorText.textContent = message;
    this.error.classList.remove('hidden');
  }

  clearError() {
    this.error?.classList.add('hidden');
    if (this.errorText) this.errorText.textContent = '';
  }

  addRipple(button, event) {
    const rect = button.getBoundingClientRect();
    const ripple = document.createElement('span');
    ripple.className = 'ppi__button-ripple';
    ripple.style.left = `${event.clientX - rect.left}px`;
    ripple.style.top = `${event.clientY - rect.top}px`;
    button.append(ripple);
    ripple.addEventListener('animationend', () => ripple.remove(), { once: true });
  }

  setupObserver() {
    if (!('IntersectionObserver' in window)) {
      this.isInView = true;
      this.classList.add('is-revealed');
      this.startGalleryAutoplay();
      this.startTrustAutoplay();
      return;
    }
    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.isInView = entry.isIntersecting;
        if (entry.isIntersecting) {
          this.classList.add('is-revealed');
          this.startGalleryAutoplay();
          this.startTrustAutoplay();
        } else {
          this.stopGalleryAutoplay();
          this.stopTrustAutoplay();
        }
      },
      { rootMargin: '140px 0px', threshold: 0.04 }
    );
    this.observer.observe(this);
  }
}

if (!customElements.get('premium-product-information')) {
  customElements.define('premium-product-information', PremiumProductInformation);
}
